import type { App } from '../app/App';
import type { NotebookCtx } from '../game/scenes/notebook/tabs';
import { stampIcon, STAMPS } from '../drawing/stamps';
import type { ShopProduct, ShopResponse } from '@shared/shop';
import { supporterLevel } from '@shared/shop';
import { cosmeticDef } from '../game/data/cosmetics';
import { needsParentGate } from '../game/systems/policy';
import { ensureOnline } from '../networking/session';
import { fetchShop, loadCart, mergeOwned, saveCart, startCartCheckout, startCheckout } from '../networking/shop';
import { MAX_CART } from '@shared/shop';
import { t } from '../i18n';
import { button, confirmBox } from './components';
import { stampFace } from './cosmetics';
import { h, setChildren } from './dom';
import { parentGate } from './family';

export const SUPPORTER_NAMES = ['', 'Bronze Supporter', 'Silver Supporter', 'Gold Supporter', 'Diamond Supporter'];

/** Buys a real-money product: a grown-up first for under-18s, then Stripe's payment page. */
export function buyPremium(app: App, p: ShopProduct): Promise<void> {
  return buyPremiumItems(app, [p]);
}

/** Buys one product or a whole cart in a single payment, with the same grown-up check. */
export async function buyPremiumItems(app: App, items: readonly ShopProduct[]): Promise<void> {
  if (items.length === 0) return;
  {
    if (needsParentGate(app.store.state) && !(await parentGate(t('Buying with real money needs a grown-up.')))) {
      app.toast(t('That answer was not right. Ask a grown-up to help.'), 'info');
      return;
    }
    if (!(await ensureOnline(app, true))) return;
    // A guest's purchases live in this account: suggest linking Google first.
    try {
      const acct = await app.api.account();
      if (acct.google === null && !(await confirmBox(t('Keep your purchases safe'), t('You are playing as a guest. Link Google in Settings → Account so you never lose what you buy. Continue anyway?'), t('Continue')))) return;
    } catch {
      /* the checkout below reports server problems */
    }
    try {
      if (items.length === 1) await startCheckout(app, items[0]!.sku);
      else await startCartCheckout(app, items.map((p) => p.sku));
    } catch (err) {
      app.toast(err instanceof Error ? err.message : t('Could not open the payment page'), 'error');
    }
  }
}

/** An ink swatch; RGB pens show their whole cycle. */
function swatch(id: string): HTMLElement {
  const def = cosmeticDef(id);
  const style = def?.cycle ? { '--ink': def.cycle[0], background: `conic-gradient(${def.cycle.join(', ')}, ${def.cycle[0]})` } : { '--ink': def?.color ?? '#000' };
  return h('span', { class: 'pen-swatch', style: style as unknown as Partial<CSSStyleDeclaration> });
}

/** The real-money shelf of the Stationery Shop: pen packs, sets, stamps and supporter tiers (Stripe). */
export function premiumShop(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const box = h('section', { class: 'premium-shop' }, h('p', { class: 'hand-hint' }, t('Loading the special shelf…')));
  const buy = (p: ShopProduct): Promise<void> => buyPremium(app, p);

  const preview = (p: ShopProduct): Node => {
    const banner = app.assets.url(`shop:${p.sku.replace(':', '_')}`);
    if (banner) return h('img', { class: 'art-img set-banner', src: banner, alt: '', draggable: false, 'aria-hidden': 'true' });
    if (p.kind === 'pens') return h('span', { class: 'pack-swatches', 'aria-hidden': 'true' }, p.grants.map(swatch));
    if (p.kind === 'set') {
      const shapes = p.grants.filter((g) => g.startsWith('brush:')).map((g) => STAMPS.find((x) => x.id === g.slice(6))).filter((x) => x !== undefined);
      return h(
        'span',
        { class: 'set-preview', 'aria-hidden': 'true' },
        shapes.map((x) => stampIcon(x.id)),
        h('span', { class: 'pack-swatches' }, p.grants.filter((g) => g.startsWith('pen:')).map(swatch)),
      );
    }
    const stamp = p.grants.map(cosmeticDef).find((d) => d?.kind === 'stamp');
    if (p.kind === 'stamp' && stamp) return stampFace(app, stamp, 'stamp-preview');
    return h('span', { class: `supporter-badge level-${p.grants.find((g) => g.startsWith('supporter:'))?.slice(10) ?? '1'}`, 'aria-hidden': 'true' }, '♥');
  };

  const render = (shop: ShopResponse): void => {
    const owned = new Set([...shop.owned, ...app.store.state.cosmetics.owned]);
    const level = supporterLevel([...owned]);
    const isOwned = (p: ShopProduct): boolean => p.kind !== 'support' && p.grants.every((g) => owned.has(g));
    // The cart: things already owned (bought elsewhere) drop out of it.
    let cart = loadCart().filter((sku) => shop.products.some((p) => p.sku === sku && !isOwned(p)));
    saveCart(cart);
    const inCart = (p: ShopProduct): boolean => cart.includes(p.sku);
    const toggle = (p: ShopProduct): void => {
      if (inCart(p)) cart = cart.filter((s) => s !== p.sku);
      else if (cart.length >= MAX_CART) return app.toast(t('The cart holds up to {n} items.', { n: MAX_CART }), 'info');
      else cart = [...cart, p.sku];
      saveCart(cart);
      render(shop);
    };
    const cartItems = (): ShopProduct[] => cart.map((s) => shop.products.find((p) => p.sku === s)).filter((p): p is ShopProduct => p !== undefined);
    const total = cartItems().reduce((sum, p) => sum + p.priceTHB, 0);
    const cartBar = cart.length
      ? h(
          'div',
          { class: 'cart-bar', role: 'region', 'aria-label': t('Shopping cart') },
          h('span', { class: 'cart-count' }, '🛒 ', t('{n} in the cart', { n: cart.length })),
          h('span', { class: 'cart-names' }, cartItems().map((p) => t(p.name)).join(', ')),
          h('b', { class: 'cart-total' }, `฿${total}`),
          button(t('Empty cart'), { small: true, variant: 'ghost', sfx: app, onClick: () => ((cart = []), saveCart(cart), render(shop)) }),
          button(t('Buy all · ฿{n}', { n: total }), { small: true, variant: 'primary', color: 'yellow', disabled: !shop.enabled, sfx: app, onClick: () => void buyPremiumItems(app, cartItems()) }),
        )
      : null;
    const card = (p: ShopProduct): HTMLElement => {
      const have = isOwned(p);
      return h(
        'li',
        { class: `cosmetic-card premium kind-${p.kind} ${have ? 'owned' : ''} ${inCart(p) ? 'in-cart' : ''}` },
        h('span', { class: 'premium-ribbon', 'aria-hidden': 'true' }, have ? '✓' : `฿${p.priceTHB}`),
        preview(p),
        h('b', { class: 'cosmetic-name' }, t(p.name)),
        h('small', { class: 'cosmetic-note' }, t(p.note)),
        have
          ? h('span', { class: 'stamp' }, t('OWNED'))
          : h(
              'div',
              { class: 'premium-actions' },
              button(`${t('Buy')} ฿${p.priceTHB}`, { small: true, color: p.kind === 'support' ? 'pink' : 'yellow', disabled: !shop.enabled, sfx: app, onClick: () => void buy(p) }),
              button(inCart(p) ? t('✓ In cart') : t('+ Cart'), { small: true, variant: inCart(p) ? 'primary' : 'ghost', color: 'blue', disabled: !shop.enabled, title: inCart(p) ? t('Take it out of the cart') : t('Add to the cart and pay for everything at once'), sfx: app, onClick: () => toggle(p) }),
            ),
      );
    };
    const shelf = (kind: ShopProduct['kind']): HTMLElement => h('ul', { class: 'cosmetic-list' }, shop.products.filter((p) => p.kind === kind).map(card));
    setChildren(
      box,
      h('h3', { class: 'section-title premium-title' }, '💎 ', t('Special Shelf'), h('span', { class: 'premium-tag' }, t('Real money'))),
      cartBar,
      h('p', { class: 'hand-hint' }, shop.enabled ? t('Paid with real money through Stripe (cards or PromptPay). Looks only — never battle power.') : t('Coming soon: the special shelf opens when payments are set up.')),
      level ? h('p', { class: 'supporter-line' }, h('span', { class: `supporter-badge level-${level}` }, '♥'), ' ', t('You are a {tier}. Thank you!', { tier: t(SUPPORTER_NAMES[level] ?? '') })) : null,
      h('h4', { class: 'shop-shelf' }, t('Themed Sets · ฿29')),
      h('p', { class: 'hand-hint' }, t('Each set gives you its drawing stamps and inks right away. You can also win them in the Paper Fortune Teller.')),
      shelf('set'),
      h('h4', { class: 'shop-shelf' }, t('Pen Packs')),
      shelf('pens'),
      h('h4', { class: 'shop-shelf' }, t('Special Stamps')),
      shelf('stamp'),
      h('h4', { class: 'shop-shelf' }, t('Support the Developer')),
      h('p', { class: 'hand-hint' }, t('Like the game? Supporting helps keep it free for everyone, and you get a badge and thank-you rewards.')),
      shelf('support'),
    );
  };

  fetchShop(app).then(
    (shop) => {
      if (mergeOwned(app, shop.owned).length) ctx.refresh();
      else render(shop);
    },
    () => setChildren(box, h('p', { class: 'hand-hint' }, t('The special shelf needs the game server.'))),
  );
  return box;
}
